"use client"

import { ReactElement, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { replaceCartWithVariant } from "@lib/data/cart"
import { DEFAULT_CURRENCY } from "@modules/analytics/lib/constants"
import { trackBeginCheckout } from "@modules/analytics/lib/pipeline"

// Entry point of the checkout funnel (Epic 3). The vitrine only hands off the
// cart; it never processes payment here. Story 3.3: "1 Plano = 1 carrinho" — adicionar
// um Plano SUBSTITUI o conteúdo do carrinho e entra no passo Identificação.
// Sem `?step=`: a página do checkout resolve o primeiro passo POR COMPLETAR (um cliente que
// volta cai direto no Pagamento em vez de clicar por passos já preenchidos).
const CHECKOUT_PATH = "/checkout"
const PLAN_QUANTITY = 1

type SubscribeButtonProps = {
  variantId: string
  countryCode: string
  // Plano em destaque usa button-filled (accent-strong); os demais, button-outline.
  featured?: boolean
}

export default function SubscribeButton({
  variantId,
  countryCode,
  featured = false,
}: SubscribeButtonProps): ReactElement {
  const router = useRouter()
  const [isSubscribing, setIsSubscribing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Synchronous re-entrancy guard: `isSubscribing` only disables the button on
  // the next render, so a fast double-click could fire `addToCart` twice (adding
  // the variant twice) before that commits. The ref blocks the second call now.
  const isSubmittingRef = useRef(false)

  const handleSubscribe = async (): Promise<void> => {
    if (isSubmittingRef.current) {
      return
    }
    isSubmittingRef.current = true
    setIsSubscribing(true)
    setErrorMessage(null)

    try {
      await replaceCartWithVariant({
        variantId,
        quantity: PLAN_QUANTITY,
        countryCode,
      })

      // begin_checkout best-effort (Story 2.6). Analítica é não-crítica e NUNCA pode
      // bloquear o addToCart/redirect (dinheiro-primeiro): exceção localizada e
      // justificada à regra "erros nunca engolidos" — engolir SÓ aqui. No-op até haver
      // provider configurado E consentimento (hoje, env vazia ⇒ nada dispara).
      try {
        trackBeginCheckout({ currency: DEFAULT_CURRENCY, itemId: variantId })
      } catch {
        // Falha de analítica é intencionalmente ignorada — não afeta a compra.
      }

      router.push(CHECKOUT_PATH)
      // Keep the loading state while navigating away so the button can't be
      // clicked twice during the redirect.
    } catch {
      setErrorMessage("Não foi possível iniciar a assinatura. Tente novamente.")
      setIsSubscribing(false)
      isSubmittingRef.current = false
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={handleSubscribe}
        disabled={isSubscribing}
        data-testid="subscribe-button"
        className={`w-full rounded-[10px] px-4 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
          featured
            ? "bg-accent-strong text-white hover:bg-blue-600"
            : "border border-white/[0.08] bg-white/[0.05] text-white/80 hover:border-accent hover:text-white"
        }`}
      >
        {/* Rótulo em PT-BR (AC3). "Contratar agora" cria o cart e entra no checkout. */}
        {isSubscribing ? "Processando..." : "Contratar agora"}
      </button>
      {errorMessage && (
        <p role="alert" className="text-sm text-danger-bright">
          {errorMessage}
        </p>
      )}
    </div>
  )
}
